import type { WidgetImage } from '@elara/domain/models/schemas/image-schemas';

import { useContext } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@elara/client-core/lib/utils';

import { ImageGalleryWidget } from '#ui/chat/ImageGalleryWidget.js';

import { CompactGuideActionsContext, GuideHeaderActionsSlot } from './guide-header-actions.js';

interface GuideLayoutProps {
  title: string;
  images?: WidgetImage[];
  metadata?: React.ReactNode;
  metadataClassName?: string;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

/**
 * A guide's photo grid. Exported for a guide that places it itself — the hotel guide puts it inside
 * its Overview tab, below the tabs — so it matches the guides that let `GuideLayout` place it.
 */
export function GuideImageGallery({ images }: { images: WidgetImage[] }) {
  return (
    <ImageGalleryWidget
      layout="grid"
      images={images}
      displayCount={images.length > 5}
      className="my-0 rounded-lg overflow-hidden"
      round="none"
      aspect="header"
    />
  );
}

export function GuideLayout({
  title,
  images,
  metadata,
  metadataClassName,
  actions,
  className,
  children,
}: GuideLayoutProps) {
  const hasImages = images && images.length > 0;
  // The panel's floating header, where the actions follow the reader once this header scrolls away.
  const headerActionsSlot = useContext(GuideHeaderActionsSlot);

  return (
    // Fills the panel's height, so a tab's empty state can sit in the middle of the space left to it.
    <div className={cn('@container relative flex flex-1 flex-col', className)}>
      <div className="flex flex-1 flex-col gap-8 w-full max-w-6xl mx-auto">
        {/* Header */}
        <div
          // A little air between the panel's floating header (the breadcrumb) and the title.
          className={cn(
            'flex flex-col @md:flex-row @md:justify-between gap-6 w-full',
            hasImages ? 'pt-2' : 'pt-6 md:pt-2',
          )}
        >
          <div className="flex flex-col gap-2 @3xl:gap-3">
            <h3 className="text-xl @xl:text-2xl @3xl:text-4xl font-display">{title}</h3>
            {metadata && (
              <div
                className={cn(
                  'flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground',
                  metadataClassName,
                )}
              >
                {metadata}
              </div>
            )}
          </div>
          {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
          {actions &&
            headerActionsSlot &&
            createPortal(
              <CompactGuideActionsContext.Provider value>{actions}</CompactGuideActionsContext.Provider>,
              headerActionsSlot,
            )}
        </div>

        {hasImages && <GuideImageGallery images={images} />}

        {children}
      </div>
    </div>
  );
}
